import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import type { HeatmapHighlight } from '@hotjar/webapp/domains/highlights/model';
import { ActionMenu, Card, tokens } from '@hotjar/hotsauce';
import { HighlightsEventType } from '@hotjar/webapp/domains/highlights/dto';
import { sendHighlightEvent } from '@hotjar/webapp/domains/highlights/api';

import { HighlightShareModal } from './Heatmaps/HeatmapShareModal';
import { HighlightSubtitle } from './HighlightSubtitle';
import { InnerBody, StyledCard } from './Shared';
import { HighlightActionDropdown } from './HighlightActionDropdown';
import { HeatmapHighlightDownload } from './List/HeatmapHighlightDownload';

const HighlightActionDropdownWithMargin = styled(HighlightActionDropdown)`
  margin-left: ${tokens.spacer.x1};
`;

interface HeatmapContainerProps {
  highlight: HeatmapHighlight;
  siteId: string;
  currentUser: { id: string };
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

export const HeatmapPopoverContainer = (props: HeatmapContainerProps): JSX.Element | null => {
  const { children, highlight, onClose, siteId, currentUser, className } = props;
  const { t } = useTranslation();
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [isDownloadVisible, setIsDownloadVisible] = useState(false);

  useEffect(() => {
    sendHighlightEvent(
      siteId,
      { highlightId: highlight.id, eventType: HighlightsEventType.HIGHLIGHT_VIEWED },
      { highlightArea: 'heatmap_area' },
    );
  }, [siteId, highlight.id]);

  const openShareModal = useCallback(() => {
    setShareModalOpen(true);
  }, [setShareModalOpen]);

  return (
    <>
      {isDownloadVisible && (
        <HeatmapHighlightDownload
          highlightId={highlight.id}
          imageUrl={highlight.imageUrl}
          selectedArea={highlight.metadata.selectedArea}
          afterDownload={() => setIsDownloadVisible(false)}
          siteId={siteId}
          trackingUiPosition="highlight_popover"
        />
      )}

      <HighlightShareModal
        siteId={siteId}
        highlight={{ id: highlight.id, fullItemUrl: highlight.fullItemUrl ?? '' }}
        isOpen={isShareModalOpen}
        onClose={() => setShareModalOpen(false)}
        uiPosition="highlight_popover"
      />

      <StyledCard
        onDismiss={onClose}
        className={`highlight-popover${className ? ` ${className}` : ''}`}
      >
        <Card.Header
          title={t('highlights:single.types.Heatmap')}
          subtitle={
            (<HighlightSubtitle siteId={siteId} highlight={highlight} />) as unknown as string
          }
        >
          <ActionMenu description={t('highlights:popover.more-options')}>
            <ActionMenu.Item
              isDisabled={isDownloadVisible}
              icon="download"
              onClick={() => {
                setIsDownloadVisible(true);
              }}
              description={t('highlights:single.download')}
            />
            <ActionMenu.Item
              icon="share"
              onClick={openShareModal}
              description={t('common:actions.share')}
            />
          </ActionMenu>
          <HighlightActionDropdownWithMargin
            siteId={siteId}
            uiPosition="highlight_popover"
            highlight={highlight}
            authorIsCurrentUser={currentUser?.id === highlight.author.id}
            onCloseDeleteModal={onClose}
          />
        </Card.Header>
        <Card.Body className="card-body">
          <InnerBody>{children}</InnerBody>
        </Card.Body>
      </StyledCard>
    </>
  );
};
